<template>
  <div :class="computedWrapperClass">
    <InstrumentAvatar
      class="instrument-name__avatar"
      :symbol="symbol"
      :type="type"
      :size="avatarSize"
    />
    <div>
      <p :class="computedTextPrimary" :title="title || symbol">
        {{ title || symbol }}
      </p>
      <p v-if="name" :title="name" :class="computedTextSecondary">
        {{ name }}
      </p>
    </div>
  </div>
</template>

<script lang="ts">
import { Component, Vue, Prop } from 'vue-property-decorator';

import { InstrumentAvatar } from '@/modules/trade/components/InstrumentAvatar';

import { InstrumentType } from '@/modules/trade/types';
import { getInstrumentSymbolPreffix } from '@/modules/trade/helpers/instruments';

export enum InstrumentNameSizes {
  SMALL = 'small',
  REGULAR = 'regular'
}

@Component({
  components: {
    InstrumentAvatar
  }
})
export default class InstrumentName extends Vue {
  @Prop({ type: String, required: true })
  readonly symbol!: string;

  @Prop({ type: String, required: false })
  readonly title?: string;

  @Prop({ type: String, required: false })
  readonly name?: string;

  @Prop({ type: String, required: false })
  readonly type?: InstrumentType;

  @Prop({ type: String, default: InstrumentNameSizes.REGULAR })
  readonly size!: string;

  @Prop({ type: Boolean, default: false })
  readonly hasEllipsisOnName!: boolean;

  @Prop({ type: Boolean, default: false })
  readonly hasEllipsisOnSymbol!: boolean;

  get computedWrapperClass() {
    return ['instrument-name', `instrument-name--${this.size}`];
  }

  get computedTextPrimary() {
    return [
      'instrument-name__text--primary',
      { 'instrument-name__text--ellipsis': this.hasEllipsisOnSymbol }
    ];
  }

  get computedTextSecondary() {
    return [
      'instrument-name__text--secondary',
      { 'instrument-name__text--ellipsis': this.hasEllipsisOnName }
    ];
  }

  get symbolPreffix() {
    return getInstrumentSymbolPreffix(this.symbol);
  }

  get avatarSize() {
    switch (this.size) {
      case InstrumentNameSizes.SMALL:
        return 'extra-small';
      case InstrumentNameSizes.REGULAR:
      default:
        return 'medium';
    }
  }
}
</script>

<style lang="less" scoped>
.instrument-name {
  display: flex;
  align-items: center;

  &__avatar {
    margin-right: @size-spacing-x300;
  }

  &__text {
    &--ellipsis {
      display: -webkit-box;
      -webkit-line-clamp: 1;
      -webkit-box-orient: vertical;
      overflow: hidden;
      word-break: break-all;
      white-space: normal !important;
    }

    &--primary {
      color: @text-primary;
      white-space: nowrap;
    }
    &--secondary {
      color: @text-secondary;
      white-space: nowrap;
    }
  }

  &--small {
    .instrument-name {
      &__text {
        &--primary {
          .text-p-5();
        }
        &--secondary {
          .text-p-6();
        }
      }
    }
  }

  &--regular {
    .instrument-name {
      &__text {
        &--primary {
          .text-p-3();
        }
        &--secondary {
          .text-p-5();
        }
      }
    }
  }
}
</style>
